import React, { useCallback, useEffect, useState } from 'react';
import { isMobile, isTablet } from 'react-device-detect';
import { connect } from 'react-redux';
import { Route, Switch, useLocation } from 'react-router-dom';
import { useIntercom } from 'react-use-intercom';
import { library } from '@fortawesome/fontawesome-svg-core';
import { fas } from '@fortawesome/free-solid-svg-icons';

import { brandConfig, lazyWithRetry } from './libs/ui/src';
import { PrivateRoute } from './components/';
import { clientActions } from './redux/client';
import { inboxActions } from './redux/inbox';
import { settingsActions } from './redux/settings';
import {CONSTANTS, isProd, StorageService} from './services';
import { refreshToken as refreshAuthToken, setAuthToken } from './services/apiService';
import { IntegrationType } from './pages/integrations/integrationsList';
import {HSOAuth2PopupPage, SFOAuth2PopupPage} from './pages/oauth2/OAuth2PopupPage';
import { SignInPage } from './pages/signin';
import { SignUpPage } from './pages/signup';
import { NotFoundPage } from './pages/notFound';

import 'antd/dist/reset.css';
import './App.scss';

const AuthorizePage = lazyWithRetry(() => import('./pages/authorize'));
const PasswordResetPage = lazyWithRetry(() => import('./pages/password'));
const UsersPage = lazyWithRetry(() => import('./pages/users'));
const CampaignsPage = lazyWithRetry(() => import('./pages/campaigns'));
const ConnectionsPage = lazyWithRetry(() => import('./pages/connections'));
const CRMPage = lazyWithRetry(() => import('./pages/crm'));
const ProspectsPage = lazyWithRetry(() => import('./pages/prospects'));
const LazyInboxPage = lazyWithRetry(() => import('./pages/inbox'));
const TemplatesPage = lazyWithRetry(() => import('./pages/templates'));
const TwitterConnectionPage = lazyWithRetry(() => import('./pages/twitter'));
const MaintenancePage = lazyWithRetry(() => import('./pages/maintenance'));
const MemberPage = lazyWithRetry(() => import('./pages/members'));
const PurchasePromoPage = lazyWithRetry(() => import('./pages/purchasePromo'));
const TasksPage = lazyWithRetry(() => import('./pages/tasks'));
const AudienceListsPage = lazyWithRetry(() => import('./pages/audiencelists'));
const LeadEnrichmentPage = lazyWithRetry(() => import('./pages/leadenrichment'));
const IntegrationsPage = lazyWithRetry(() => import('./pages/integrations'));
const IntegrationSettings = lazyWithRetry(() => import('./pages/integrations/IntegrationSettings'));
const SlackOauth2PopupPage = lazyWithRetry(() => import('./pages/oauth2/SlackOauth2PopupPage'));
const ProductDemosPage = lazyWithRetry(() => import('./pages/demos'));
const LeadListDetailPage = lazyWithRetry(() => import('./pages/leadlistdetail'));
const LeadEnrichmentDetailPage = lazyWithRetry(() => import('./pages/leadenrichmentdetail'));

library.add(fas);

interface AppProps {
  client: any;
  users: [] | undefined;
  campaigns: [] | undefined;
  isAdmin: boolean;
  isMaintenance: boolean;
  getClientAndInitialData: () => void;
  updateClientIfNeeded: () => void;
  getInboxUnreadCountByUser: () => void;
  setSettings: (params: any) => void;
  logout: (unauthorizedError?: boolean) => void;
}

const App = ({
  client,
  users,
  campaigns,
  isAdmin,
  isMaintenance,
  getClientAndInitialData,
  getInboxUnreadCountByUser,
  updateClientIfNeeded,
  setSettings,
  logout,
}: AppProps) => {
  const [userUpdateTimer, setUserUpdateTimer] = useState(null);
  const [isIntercomBooted, setIsIntercomBooted] = useState(false);
  const location = useLocation();
  const [user, setUser] = useState({ name: '', email: '', userId: null });

  const { boot, shutdown } = useIntercom();

  const getClientRequest = () => {
    // TODO check if we need this
    const isPublicRoute = location.pathname.match(/(team-member-invite)/g);
    if (!isPublicRoute) {
      getClientAndInitialData();
    }
  };

  // deprecated function
  // const saveTrackingCodeToCookies = () => {
  //   const params = new URLSearchParams(location.search);
  //
  //   const source = params.get('source');
  //   if (!source || source !== 'growth-x') {
  //     // this is not UTM field that should be saved to cookies
  //     return;
  //   }
  //
  //   const utm_campaign = params.get('utm_campaign');
  //   if (utm_campaign) {
  //     try {
  //       const hostname = window.location.hostname;
  //       if (hostname.includes('localhost')) return;
  //       const parentDomain = hostname.split('.').reverse()[1] + '.' + hostname.split('.').reverse()[0];
  //       document.cookie = 'utm_campaign=' + utm_campaign + '; domain=.' + parentDomain + '; path=/';
  //     } catch (err) {
  //       console.log(err);
  //     }
  //   }
  // };

  const parseUTMParams = () => {
    try {
      const params = new URLSearchParams(location.search);
      const utmKeys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'utm_id'];
      if (params.has('utm_source')) {
        const utmParams = {};
        utmKeys.forEach(key => {
          if (params.has(key)) {
            utmParams[key] = params.get(key);
          }
        });
        StorageService.setItem('utm_params', JSON.stringify(utmParams));
      }
    } catch (err) {
      console.error('Failed to parse UTM params', err);
    }
  }

  const setManifestJson = () => {
    document.title = brandConfig.documentTitle;
    document
      .querySelector('#my-manifest-placeholder')
      .setAttribute('href', `public/${brandConfig.brand}/manifest.json`);
    document
      .querySelector('#description')
      .setAttribute('content', `${brandConfig.name} is an automation tool for social media outreach.`);
    document.querySelector('#author').setAttribute('content', brandConfig.name);
    document
      .querySelector('#shortcutIcon')
      .setAttribute('href', `public/${brandConfig.brand}/${brandConfig.brand}.png`);
    document
      .querySelector('#apple-touch-icon')
      .setAttribute('href', `public/${brandConfig.brand}/${brandConfig.brand}128.png`);
    brandConfig?.variables?.forEach(v => {
      document.querySelector('body').style.setProperty(v.name, v.value);
    });
  };

  useEffect(() => {
    if (brandConfig.isGx && client && isProd()) {
      const { host, pathname } = window.location;
      if (client.beta && !host.includes('beta')) {
        const newUrl = `${CONSTANTS.beta_url}${pathname}`;
        window.location.replace(newUrl);
        return;
      }
      if (!client.beta && host.includes('beta')) {
        const newUrl = `${CONSTANTS.app_url}${pathname}`;
        window.location.replace(newUrl);
        return;
      }
    }
  }, [client]);

  useEffect(() => {
    setManifestJson();
    getClientRequest();
    // saveTrackingCodeToCookies();
    parseUTMParams();
    if (isMobile && !isTablet) {
      setSettings({ isNavCollapsed: true });
    }
    const timer = setInterval(() => {
      updateClientIfNeeded();
      getInboxUnreadCountByUser();
    }, CONSTANTS.limits.client_update_interval); // update client every two minutes
    setUserUpdateTimer(timer);
    return () => clearInterval(userUpdateTimer);
  }, []);

  // Refresh ML auth token every 10 minutes when logged in and a refresh token exists
  useEffect(() => {
    const refresh = async () => {
      try {
        const response = await refreshAuthToken();
        const data = response?.data;
        if (data?.access_token) {
          setAuthToken(data.access_token);
        }
      } catch (err: any) {
        if (err?.response?.status === 401) {
          logout(true);
        }
      }
    };

    const intervalId = setInterval(refresh, 4 * 60 * 1000);
    return () => clearInterval(intervalId);
  }, []);

  useEffect(() => {
    setUser(getUserData(client));
  }, [client]);

  useEffect(() => {
    if (brandConfig.intercomFeature) {
      try {
        if (user.name !== '' && !isAdmin) {
          boot({ ...user });
          setIsIntercomBooted(true);
        } else {
          if (isIntercomBooted) {
            shutdown();
            setIsIntercomBooted(false);
          }
        }
      } catch (err) {
        console.error('Intercom error', err);
      }
    }
  }, [user, isAdmin]);

  const getUserData = useCallback(
    (client: any) => ({
      name: client ? client.name : '',
      email: client ? client.email : '',
      userId: client ? client.id : '',
    }),
    []
  );

  if (isMaintenance) return <MaintenancePage />;

  return (
    <div className="appRoot">
      <Switch>
        <Route exact path="/login" component={SignInPage} />
        <Route exact path="/signup" component={SignUpPage} />
        <Route exact path="/oauth2/authorize" component={AuthorizePage} />
        {/*<Route exact path="/signup" component={() => <SignInPage isSignup={true} />} />*/}
        <Route
          path="/twitter-integration/:connected"
          component={(props: any) => <TwitterConnectionPage success={props.match.params.connected === 'connected'} />}
        />
        <Route path="/team-member-invite" component={MemberPage} />
        <Route exact path="/purchase-promo" component={PurchasePromoPage} />
        {!brandConfig.isAffiliate && <Route exact path="/reset-password" component={PasswordResetPage} />}
        {!brandConfig.isAffiliate && <Route exact path="/set-password" component={PasswordResetPage} />}
        <PrivateRoute
          exact
          path="/"
          component={CampaignsPage}
          users={users}
          client={client}
          isLoading={!client || !users}
        />
        <PrivateRoute
          path="/audience-lists"
          exact
          component={AudienceListsPage}
          users={users}
          client={client}
          isLoading={!client || !users}
        />
        <PrivateRoute
          path="/audience-lists/:listId"
          component={LeadListDetailPage}
          users={users}
          client={client}
          isLoading={!client || !users}
        />
        <PrivateRoute
          path="/lead-enrichment"
          exact
          component={LeadEnrichmentPage}
          users={users}
          client={client}
          isLoading={!client || !users}
        />
        <PrivateRoute
          path="/lead-enrichment/:listId"
          component={LeadEnrichmentDetailPage}
          users={users}
          client={client}
          isLoading={!client || !users}
        />
        <PrivateRoute path="/users" component={UsersPage} users={users} client={client} isLoading={!client || !users} />
        <PrivateRoute
          path="/prospects"
          component={ProspectsPage}
          users={users}
          client={client}
          isLoading={!client || !users}
        />
        <PrivateRoute
          path="/connections"
          component={ConnectionsPage}
          users={users}
          client={client}
          isLoading={!client || !users}
        />
        <PrivateRoute
          path="/crm"
          component={CRMPage}
          users={users}
          client={client}
          isLoading={!client || !users}
        />
        <PrivateRoute
          path="/inbox/:user?"
          component={LazyInboxPage}
          users={users}
          client={client}
          isLoading={!client || !users || !campaigns}
        />
        <PrivateRoute
          path="/templates"
          component={TemplatesPage}
          users={users}
          client={client}
          isLoading={!client || !users || !campaigns}
        />
        <PrivateRoute
          path="/tasks"
          component={TasksPage}
          users={users}
          client={client}
          isLoading={!client || !users || !campaigns}
        />
        <Route exact path="/integrations/slack/callback" component={SlackOauth2PopupPage} />
        <PrivateRoute
          path="/integrations"
          exact
          component={IntegrationsPage}
          users={users}
          client={client}
          isLoading={!client || !users}
        />
        <PrivateRoute
          path={`/integrations/:integrationId(${Object.values(IntegrationType).join('|')})`}
          component={IntegrationSettings}
          users={users}
          client={client}
          isLoading={!client || !users}
        />
        <PrivateRoute
          path="/demos"
          component={ProductDemosPage}
          users={users}
          client={client}
          isLoading={!client || !users}
        />
        <PrivateRoute
          path="/oauth2/callback"
          component={SFOAuth2PopupPage}
          users={users}
          client={client}
        />
        <PrivateRoute
          path="/hs/oauth/callback"
          component={HSOAuth2PopupPage}
          users={users}
          client={client}
        />
        <Route path="*" component={NotFoundPage} />
      </Switch>
    </div>
  );
};

function mapState(state: any) {
  return {
    client: state.client.data,
    users: state.users.list,
    campaigns: state.campaigns.list,
    isAdmin: state.settings.isAdmin,
    isMaintenance: state.maintenance.isMaintenance,
  };
}

const actionCreators = {
  getClientAndInitialData: clientActions.getClientAndInitialData,
  updateClientIfNeeded: clientActions.updateClientIfNeeded,
  setSettings: settingsActions.setSettings,
  getInboxUnreadCountByUser: inboxActions.getInboxUnreadCountByUser,
  logout: clientActions.logout,
};

const ConnectedApp = connect(mapState, actionCreators)(App);

export default ConnectedApp;
